import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as authApi from '@/apis/auth';
import { authAtom } from '@/atom/authAtom';
import { useAtom } from 'jotai';
import { removeItem, setItem } from '@/common/localStorage';
import { adminApi } from '@/apis/api';
import { parseJwtToken } from '@/common/parseToken';

// 쿼리 키 상수화
export const AUTH_KEYS = {
  all: ['auth'],
  me: ['myInfo'],
};

/**
 * verify-otp(또는 기존 단일 로그인) 응답에서 JWT를 꺼내 auth·localStorage·헤더에 반영
 */
function applyAuthFromVerifyResponse(axiosResponse, setAuth, queryClient) {
  const { headers } = axiosResponse;
  const responseToken = headers.authorization;
  const refreshToken = headers.refresh;

  if (!responseToken) {
    throw new Error('인증 토큰을 받지 못했습니다.');
  }

  const tokenMatch = responseToken.match(/Bearer\s+(.+)/);
  if (!tokenMatch || !tokenMatch[1]) {
    throw new Error('토큰 형식이 올바르지 않습니다.');
  }

  const token = tokenMatch[1];
  const { username, role, userId } = parseJwtToken(token);

  setItem('token', responseToken);
  if (refreshToken) {
    setItem('refreshToken', refreshToken);
  }

  setAuth({
    userId,
    username,
    role,
    token: responseToken,
    refreshToken: refreshToken || '',
  });

  queryClient.invalidateQueries({ queryKey: AUTH_KEYS.me });
}

/* ==========================================
    Queries (GET)
========================================== */

// 1. 내 정보 조회
export const useMyInfo = () => {
  const [auth] = useAtom(authAtom);

  return useQuery({
    queryKey: AUTH_KEYS.me,
    queryFn: authApi.fetchMyInfo,
    enabled: !!auth.token,
    refetchOnWindowFocus: true,
    retry: false, // 로그인 정보 실패시 바로 튕겨내기 위해
  });
};

/* ==========================================
    Mutations (POST, PUT, PATCH, DELETE)
========================================== */

/**
 * 1단계 로그인: 아이디/비밀번호 → 202 + preAuthToken (이메일 OTP 발송)
 */
export const useLoginRequest = () => {
  return useMutation({
    mutationFn: async ({ username, password }) => {
      const res = await authApi.fetchLogin({ username, password });
      if (res.status !== 202) {
        throw new Error('예상치 못한 응답입니다.');
      }
      const payload = res.data;
      const preAuthToken = payload?.data?.preAuthToken;
      if (!preAuthToken) {
        throw new Error('인증 준비 토큰을 받지 못했습니다.');
      }
      return {
        preAuthToken,
        message: payload?.message || payload?.msg || '',
      };
    },
  });
};

/**
 * 2단계: OTP 검증 → 200 + Authorization 헤더(JWT)
 */
export const useVerifyOtp = () => {
  const [, setAuth] = useAtom(authAtom);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => authApi.fetchVerifyOtp(body),
    onSuccess: (axiosResponse) => {
      try {
        applyAuthFromVerifyResponse(axiosResponse, setAuth, queryClient);
      } catch (error) {
        console.error('OTP 검증 후 로그인 처리 중 오류:', error);
        throw error;
      }
    },
  });
};

// 2. 로그아웃
export const useLogout = () => {
  const queryClient = useQueryClient();
  const setAuth = useAtom(authAtom)[1];

  // 인증 상태 초기화 함수
  const resetAuth = () => {
    const defaultAuthState = {
      userId: '',
      username: '',
      role: 1,
      token: '',
      refreshToken: '',
    };
    setAuth(defaultAuthState);
  };

  // 로그아웃 정리 작업 (성공/실패 공통)
  const cleanup = () => {
    // 인증 상태 초기화 (authAtom 리셋)
    // authAtom의 setItem에서 자동으로 localStorage 정리 및 Authorization 헤더 제거가 수행됨
    resetAuth();

    // localStorage 토큰 제거 (이중 안전장치)
    removeItem('token');
    removeItem('refreshToken');

    // 모든 쿼리 무효화 및 제거
    queryClient.invalidateQueries({ queryKey: AUTH_KEYS.all });
    queryClient.removeQueries({ queryKey: AUTH_KEYS.me });
  };

  return useMutation({
    mutationFn: authApi.fetchLogout,
    onSuccess: (data) => {
      // 서버 응답 확인 (code === 1이면 성공)
      if (data?.data?.code === 1) {
        cleanup();
      } else {
        // 서버에서 실패 응답이 와도 클라이언트 정리는 수행
        cleanup();
      }
    },
    onError: (error) => {
      // 에러가 발생해도 클라이언트 정리는 수행 (네트워크 오류 등)
      cleanup();
    },
  });
};
